"use client";

import { type ReactNode, memo, useContext, useMemo } from "react";
import { Virtuoso } from "react-virtuoso";
import { VirtuosoErrorBoundary } from "@/components/virtuoso-error-boundary";
import { cn } from "@/lib/utils";
import { useRow } from "@/hooks/use-odds-live";
import { ladderEventOf } from "@/lib/odds-screen/live/inplay";
import type { BoardView, BookId, EventHeader, FamilyId, FamilySummary, LeagueId, MarketSpec, OddsDisplay, RowId } from "@/lib/odds-screen/live/types";
import { lineSides, marketCells, rowCells, type CellSide } from "./row-model";
import { BetChip, BookStakeContext, StakeText, stakeLabel } from "./price-cell";
import { PropsLink, propsOffer, useCellBet, type CellBet, type CellTarget, type HiddenRow } from "./board-rows";
import type { BetActions } from "./bet";
import { EventGroupHeader, KickoffHeader } from "./group-headers";
import { LineTabs, RailColumns, useRailModel, type BookStake, type HasAccount, type RailLinkClick } from "./drill-rail";
import { marketDisplayOrder } from "./view-model";
import { SheetShell } from "./top-menus";
import { drawName, formatStake, matchupLabel, matchupSides, outcomeDisplayOrder, outcomeName, rowLines, teamLogoUrl, type MatchupSide } from "./labels";
import { KICKOFF_TIME, clock, metaDate, startedAt } from "./time";
import { LatestPlay, LiveState, PeriodStrip, SideScore, hasPlayOrPeriods, sideTone } from "./live-state";
import type { DrillTarget } from "./drill";
import { LineHistoryRow } from "./line-history-row";
import type { OpenOddsHistory } from "@/components/odds/odds-history-selection";
import { SubjectCell } from "./subject-cell";
import { countNoun, eventGroups, eventItems, kickoffItems, rowSpec, subjectKind, subjectOf, subjectShape, type EventGroup, type EventListItem, type KickoffListItem } from "./subject";
import { PlayerAvatar, TeamLogo } from "@/components/ui/avatar";
import { StarButton, useStars } from "./star-button";
import { MinStakeContext, belowMinText } from "./use-min-stake";
const MOBILE_LINES_COLS = "minmax(38%,1fr) repeat(3, minmax(52px,72px))";
const PHONE_MAX_PERIOD_CELLS = 4;

type Drill = { readonly rowId: RowId; readonly slot: number; readonly outcome: number } | null;

function MobileCell({ side, label, selected, onOpen, bet, target }: { side: CellSide | undefined; label?: string; selected: boolean; onOpen: () => void; bet: CellBet; target: CellTarget | null }) {
  const kinds = useContext(BookStakeContext);
  const minStake = useContext(MinStakeContext);
  if (side?.best === null && side.belowMin) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${side.sideLabel}, ${belowMinText(minStake).toLowerCase()}. Compare all books`}
        title={belowMinText(minStake)}
        className={cn("flex h-11 w-full flex-col items-center justify-center gap-px rounded-lg border border-transparent bg-white/[0.02] px-1 text-zinc-600 active:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40", selected && "border-primary/55 bg-primary/10")}
      >
        {label !== undefined && <span className="max-w-full truncate text-[11px] leading-[14px]">{label}</span>}
        <span className="text-[13px] leading-4">&mdash;</span>
      </button>
    );
  }
  if (side === undefined || side.best === null || target === null) {
    return (
      <div className={cn("h-11 rounded-lg bg-white/[0.02]", label !== undefined && "flex items-center justify-center px-1")} aria-hidden="true">
        {label !== undefined && <span title={label} className="truncate text-[11px] text-zinc-600">{label}</span>}
      </div>
    );
  }
  const slot = label ?? side.lineLabel;
  const wide = label !== undefined;
  const held = side.best.held === true;
  const choice = bet.pick(side.best.books);
  const stake = held ? null : formatStake(side.best.stakes.get(choice.book), kinds.get(choice.book) ?? null);
  return (
    <div
      className={cn("relative grid h-11 overflow-hidden rounded-lg border border-transparent bg-white/[0.035]", selected && "border-primary/55 bg-primary/10")}
      style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${side.sideLabel}, ${held ? "paused at" : "best"} ${side.display}${stakeLabel(stake)}. Compare all books`}
        className="flex min-w-0 flex-col items-center justify-center gap-px px-1 active:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
      >
        {slot && <span title={label} className={cn("data-num text-[11px] leading-[14px]", held ? "text-zinc-600" : "text-zinc-400", wide && "max-w-full truncate")}>{slot}</span>}
        <span className={cn("data-num text-[13px] font-semibold leading-4", held ? "text-zinc-500" : "text-foreground")}>{side.display}</span>
        <StakeText stake={stake} />
      </button>
      <BetChip
        books={side.best.books}
        pick={choice}
        subject={bet.subject}
        what={`${side.sideLabel} ${side.display}`}
        logoSize={14}
        ringColor={selected ? "#141b20" : "#16171a"}
        max={wide ? 2 : 1}
        stacked
        held={held}
        className="h-full min-w-7 border-l border-white/[0.06] px-1.5 active:bg-white/[0.08]"
        onBet={() => bet.onBet(target, choice.book)}
        onPrefetch={bet.onPrefetch}
      />
    </div>
  );
}

const MobileGameCard = memo(function MobileGameCard({
  league, family, rowId, event, specs, order, selected, format, drill, bet, onOpen, onHide, onOpenGame,
}: {
  league: LeagueId;
  family: FamilyId;
  rowId: RowId;
  event: EventHeader | undefined;
  specs: readonly MarketSpec[];
  order: readonly number[];
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: Drill;
  bet: BetActions;
  onOpen: (rowId: RowId, slot: number, outcome: number, main: number | null) => void;
  onHide: (row: HiddenRow) => void;
  onOpenGame: (rowId: RowId) => void;
}) {
  const row = useRow(league, family, rowId, event, specs);
  const minStake = useContext(MinStakeContext);
  const markets = row ? rowCells(row, specs, event, selected, format, minStake) : [];
  const cellBet = useCellBet(bet, { kind: "ladder", league, event: ladderEventOf(event), family, entity: row?.entity?.key ?? "" }, markets, row?.entity?.name ?? matchupLabel(event, league, "spoken"), (target) => onOpen(rowId, target.slot, target.outcome, target.main));
  const rowSelected = drill?.rowId === rowId;
  const firstSlot = order[0] ?? 0;
  const live = event?.live;
  const offer = propsOffer(event);
  const playOrPeriods = hasPlayOrPeriods(live);
  const entityName = row?.entity?.name ?? null;
  const matchup = matchupLabel(event, league);
  const spokenMatchup = matchupLabel(event, league, "spoken");
  const lines = rowLines(specs, entityName !== null);
  const teams: readonly MatchupSide[] = entityName === null
    ? matchupSides(event, lines, drawName(specs))
    : [{ line: "away", name: entityName, logo: teamLogoUrl(event, entityName), live: null }, { line: "home", name: "", logo: null, live: null }];
  const openGame = (si: number) => {
    const market = markets[firstSlot];
    const side = market?.sides[lineSides(market, lines)[si] ?? 0];
    onOpen(rowId, firstSlot, side?.outcome ?? 0, market?.main ?? null);
  };
  const statusRow = live && event && (
    <>
      {offer === null && <StarButton league={league} event={event} className="-my-1 -ml-2.5 h-7 w-9" />}
      <LiveState live={live} />
    </>
  );
  return (
    <article
      aria-label={spokenMatchup || undefined}
      onKeyDown={(e) => { if ((e.key === "h" || e.key === "H") && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onHide({ rowId, event, entityName }); } }}
      className={cn("rounded-[10px] border border-white/[0.06] px-2 pb-2.5 pt-1.5", rowSelected ? "bg-primary/[0.04]" : "bg-surface-raised")}
    >
      {offer !== null && (
        <div className="-mr-1 -mt-1 flex justify-end">
          <StarButton league={league} event={event} className="-ml-2 mr-auto h-9 w-9" />
          <PropsLink count={offer.count} gameLabel={spokenMatchup || "this game"} isMobile onOpen={() => onOpenGame(rowId)} />
        </div>
      )}
      {live && event && (
        <div className={cn("flex items-center px-0.5 pb-1", playOrPeriods && "flex-col items-start gap-1")}>
          {playOrPeriods ? <div className="flex items-center">{statusRow}</div> : statusRow}
          <LatestPlay play={live.plays[0] ?? null} className="max-w-full" />
        </div>
      )}
      {offer === null && !live && <StarButton league={league} event={event} className="-ml-2 -mt-1 h-9 w-9" />}
      <div className="flex flex-col gap-1">
        {teams.map(({ line, name, logo, live: side }, si) => {
          const label = <span className={cn("line-clamp-2 break-words text-[13px] font-semibold leading-[15px]", line === "draw" ? "pl-[22px] text-zinc-400" : sideTone(side))}>{name}</span>;
          return (
            <div key={line} className="grid items-center gap-1" style={{ gridTemplateColumns: MOBILE_LINES_COLS }}>
              <button type="button" onClick={() => openGame(si)} aria-label={event ? `Open ${entityName ?? name}${side?.score == null ? "" : ` ${side.score}`}` : "Open game"} className="flex min-h-11 min-w-0 items-center gap-1 text-left">
                {event && name !== "" && line !== "draw" && <TeamLogo name={name} imageUrl={logo} size={18} />}
                {name === "" ? (
                  <span className="truncate text-[11px] text-zinc-500">{matchup}</span>
                ) : playOrPeriods ? (
                  <span className="flex min-w-0 flex-col gap-0.5">
                    {label}
                    {(side?.periods?.length ?? 0) <= PHONE_MAX_PERIOD_CELLS && <PeriodStrip live={side} className="leading-4" />}
                  </span>
                ) : label}
                <SideScore live={side} inlinePeriods={false} />
              </button>
              {order.map((slot) => {
                const market = markets[slot] ?? null;
                const index = market === null ? null : lineSides(market, lines)[si];
                const side = market !== null && index !== null ? market.sides[index] : undefined;
                return (
                  <MobileCell
                    key={slot}
                    side={side}
                    selected={rowSelected && drill?.slot === slot && drill?.outcome === side?.outcome}
                    onOpen={() => side && market && onOpen(rowId, slot, side.outcome, market.main)}
                    bet={cellBet}
                    target={side && market ? { slot, outcome: side.outcome, main: market.main } : null}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
    </article>
  );
});

export function MobileLinesBoard({
  league, family, specs, view, rowIds, showDate, selected, format, drill, bet, onOpen, onHide, onOpenGame,
}: {
  league: LeagueId;
  family: FamilyId;
  specs: readonly MarketSpec[];
  view: BoardView;
  rowIds: readonly RowId[];
  showDate: boolean;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: Drill;
  bet: BetActions;
  onOpen: (rowId: RowId, slot: number, outcome: number, main: number | null) => void;
  onHide: (row: HiddenRow) => void;
  onOpenGame: (rowId: RowId) => void;
}) {
  const order = useMemo(() => marketDisplayOrder(specs), [specs]);
  const stars = useStars();
  const items = useMemo(() => kickoffItems(view, rowIds, stars), [rowIds, view, stars]);

  return (
    <div className="flex min-h-0 flex-grow flex-col">
      <div className="grid h-[30px] flex-shrink-0 items-center gap-1 border-b border-white/[0.06] bg-surface-base px-[21px]" style={{ gridTemplateColumns: MOBILE_LINES_COLS }}>
        <span className="text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">Teams</span>
        {order.map((slot) => (
          <span key={slot} className="text-center text-[10px] font-medium uppercase tracking-[0.06em] text-zinc-500">{specs[slot].label}</span>
        ))}
      </div>
      <VirtuosoErrorBoundary>
        <Virtuoso
          className="scrollbar-custom scrollbar-color-primary"
          style={{ height: "100%", width: "100%" }}
          data={items}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          computeItemKey={(index, item) => (item.kind === "kickoff" ? `g:${item.startMs}:${index}` : `r:${item.rowId}`)}
          components={{ Footer: () => <div className="h-3" /> }}
          itemContent={(_, item) => {
            if (item.kind === "kickoff") return <KickoffHeader startMs={item.startMs} showDate={showDate} starred={item.starred} variant="card" />;
            const ref = view.rows.get(item.rowId);
            const event = ref ? view.events.get(ref.eventId) : undefined;
            return (
              <div className="px-3 pb-2">
                <MobileGameCard league={league} family={family} rowId={item.rowId} event={event} specs={specs} order={order} selected={selected} format={format} drill={drill} bet={bet} onOpen={onOpen} onHide={onHide} onOpenGame={onOpenGame} />
              </div>
            );
          }}
        />
      </VirtuosoErrorBoundary>
    </div>
  );
}

const MobileSubjectCard = memo(function MobileSubjectCard({
  league, family, rowId, event, selected, format, drill, bet, onOpen, onHide,
}: {
  league: LeagueId;
  family: FamilySummary;
  rowId: RowId;
  event: EventHeader | undefined;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: Drill;
  bet: BetActions;
  onOpen: (rowId: RowId, slot: number, outcome: number, main: number | null) => void;
  onHide: (row: HiddenRow) => void;
}) {
  const spec = family.markets[0];
  const row = useRow(league, family.id, rowId, event, family.markets);
  const minStake = useContext(MinStakeContext);
  const market = row === undefined ? null : marketCells(row.markets[0] ?? null, spec, event, selected, format, minStake);
  const cellBet = useCellBet(bet, { kind: "ladder", league, event: ladderEventOf(event), family: family.id, entity: row?.entity?.key ?? "" }, [market], row?.entity?.name ?? matchupLabel(event, league, "spoken"), (target) => onOpen(rowId, target.slot, target.outcome, target.main));
  const subject = subjectOf(family, row, event, league);
  const rowSelected = drill?.rowId === rowId;
  const outcomes = outcomeDisplayOrder(spec);
  return (
    <article
      aria-label={subject.kind === "game" ? matchupLabel(event, league, "spoken") || undefined : subject.name}
      onKeyDown={(e) => { if ((e.key === "h" || e.key === "H") && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onHide({ rowId, event, entityName: row?.entity?.name ?? null }); } }}
      className={cn("rounded-[10px] border border-white/[0.06] px-2 pb-2.5 pt-1.5", rowSelected ? "bg-primary/[0.04]" : "bg-surface-raised")}
    >
      <SubjectCell subject={subject} className="min-h-11 pl-1" />
      <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${outcomes.length}, minmax(0,1fr))` }}>
        {outcomes.map((outcome) => {
          const side = market?.sides.find((entry) => entry.outcome === outcome);
          return (
            <MobileCell
              key={outcome}
              side={side}
              label={outcomeName(spec, event, outcome)}
              selected={rowSelected && drill?.outcome === outcome}
              onOpen={() => side && market && onOpen(rowId, 0, outcome, market.main)}
              bet={cellBet}
              target={side && market ? { slot: 0, outcome, main: market.main } : null}
            />
          );
        })}
      </div>
    </article>
  );
});

export function MobileSubjectBoard({
  league, family, view, rowIds, showDate, selected, format, drill, collapsed, bet, onToggle, onOpen, onHide,
}: {
  league: LeagueId;
  family: FamilySummary;
  view: BoardView;
  rowIds: readonly RowId[];
  showDate: boolean;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: Drill;
  collapsed: ReadonlySet<string>;
  bet: BetActions;
  onToggle: (eventId: string) => void;
  onOpen: (rowId: RowId, slot: number, outcome: number, main: number | null) => void;
  onHide: (row: HiddenRow) => void;
}) {
  const { grouping } = subjectShape(family);
  const noun = countNoun(family);
  const items = useMemo<readonly (EventListItem | KickoffListItem)[]>(() => (grouping === "event" ? eventItems(view, rowIds, collapsed, true, league) : kickoffItems(view, rowIds)), [view, rowIds, grouping, collapsed, league]);
  return (
    <div className="flex min-h-0 flex-grow flex-col">
      <VirtuosoErrorBoundary>
        <Virtuoso
          className="scrollbar-custom scrollbar-color-primary"
          style={{ height: "100%", width: "100%" }}
          data={items}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          computeItemKey={(index, item) => (item.kind === "event" ? `g:${item.eventId}` : item.kind === "kickoff" ? `k:${index}` : `r:${item.rowId}`)}
          components={{ Footer: () => <div className="h-3" /> }}
          itemContent={(_, item) => {
            if (item.kind === "event") {
              return <EventGroupHeader label={item.label} time={item.time} count={item.count} noun={noun} collapsed={item.collapsed} compact onToggle={() => onToggle(item.eventId)} />;
            }
            if (item.kind === "kickoff") return <KickoffHeader startMs={item.startMs} showDate={showDate} variant="card" />;
            const ref = view.rows.get(item.rowId);
            return (
              <div className={cn("px-3 pb-2", grouping === "event" && "pt-2")}>
                <MobileSubjectCard league={league} family={family} rowId={item.rowId} event={ref ? view.events.get(ref.eventId) : undefined} selected={selected} format={format} drill={drill} bet={bet} onOpen={onOpen} onHide={onHide} />
              </div>
            );
          }}
        />
      </VirtuosoErrorBoundary>
    </div>
  );
}

type ResultListItem =
  | { readonly kind: "event"; readonly group: EventGroup }
  | { readonly kind: "row"; readonly rowId: RowId; readonly eventId: string; readonly last: boolean };

function resultItems(groups: readonly EventGroup[]): readonly ResultListItem[] {
  return groups.flatMap((group): ResultListItem[] => [
    { kind: "event", group },
    ...(group.collapsed ? [] : group.rowIds.map((rowId, index): ResultListItem => ({ kind: "row", rowId, eventId: group.eventId, last: index === group.rowIds.length - 1 }))),
  ]);
}

const MobileResultRow = memo(function MobileResultRow({
  league, family, rowId, event, selected, format, drill, last, bet, onOpen, onHide,
}: {
  league: LeagueId;
  family: FamilySummary;
  rowId: RowId;
  event: EventHeader | undefined;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: Drill;
  last: boolean;
  bet: BetActions;
  onOpen: (rowId: RowId, slot: number, outcome: number, main: number | null) => void;
  onHide: (row: HiddenRow) => void;
}) {
  const kinds = useContext(BookStakeContext);
  const minStake = useContext(MinStakeContext);
  const row = useRow(league, family.id, rowId, event, family.markets);
  const market = row === undefined ? null : marketCells(row.markets[0] ?? null, rowSpec(family, row), event, selected, format, minStake);
  const cellBet = useCellBet(bet, { kind: "ladder", league, event: ladderEventOf(event), family: family.id, entity: row?.entity?.key ?? "" }, [market], row?.entity?.name ?? matchupLabel(event, league, "spoken"), (target) => onOpen(rowId, target.slot, target.outcome, target.main));
  const side = market?.sides[0];
  const subject = subjectOf(family, row, event, league);
  const name = subject.kind === "player" || subject.kind === "result" ? subject.name : "";
  const rowSelected = drill?.rowId === rowId;
  const choice = side?.best ? cellBet.pick(side.best.books) : null;
  const stake = side?.best && choice ? formatStake(side.best.stakes.get(choice.book), kinds.get(choice.book) ?? null) : null;
  const label = (
    <>
      {subject.kind === "player" && <PlayerAvatar name={subject.name} imageUrl={subject.imageUrl} size={28} />}
      <span className="line-clamp-2 min-w-0 flex-grow break-words text-[13px] font-medium leading-[16px] text-foreground">{name}</span>
    </>
  );
  return (
    <div className={cn("px-3", last && "pb-2")} onKeyDown={(e) => { if ((e.key === "h" || e.key === "H") && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onHide({ rowId, event, entityName: row?.entity?.name ?? null }); } }}>
      <div className={cn("border-x border-white/[0.06]", last ? "rounded-b-[10px] border-b" : "border-b", rowSelected ? "bg-primary/[0.04]" : "bg-surface-raised")}>
        {side === undefined || (side.best === null && !side.belowMin) ? (
          <div aria-label={`${name} not offered`} className="flex min-h-11 items-center gap-2 px-3 opacity-50">{label}</div>
        ) : side.best === null ? (
          <button
            type="button"
            onClick={() => market && onOpen(rowId, 0, side.outcome, market.main)}
            aria-label={`${name}, ${belowMinText(minStake).toLowerCase()}. Compare all books`}
            title={belowMinText(minStake)}
            className="flex min-h-11 w-full items-center gap-2 px-3 text-left opacity-50 active:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
          >
            {label}
            <span className="flex-shrink-0 text-[13px] text-zinc-600">&mdash;</span>
          </button>
        ) : (
          <div className="flex min-h-11 items-stretch">
            <button
              type="button"
              onClick={() => market && onOpen(rowId, 0, side.outcome, market.main)}
              aria-label={`${side.sideLabel}, best ${side.display}${stakeLabel(stake)}. Compare all books`}
              className="flex min-w-0 flex-grow items-center gap-2 pl-3 pr-2 text-left active:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40"
            >
              {label}
              <span className="flex flex-shrink-0 flex-col items-end">
                <span className="data-num text-[13px] font-semibold leading-4 text-foreground">{side.display}</span>
                <StakeText stake={stake} />
              </span>
            </button>
            {market && choice && (
              <BetChip
                books={side.best.books}
                pick={choice}
                subject={cellBet.subject}
                what={`${side.sideLabel} ${side.display}`}
                logoSize={14}
                ringColor="#16171a"
                max={2}
                className="min-w-[60px] border-l border-white/[0.06] px-3 active:bg-white/[0.06]"
                onBet={() => cellBet.onBet({ slot: 0, outcome: side.outcome, main: market.main }, choice.book)}
                onPrefetch={cellBet.onPrefetch}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export function MobileResultsBoard({
  league, family, view, rowIds, showDate, selected, format, drill, collapsed, bet, onToggle, onOpen, onHide,
}: {
  league: LeagueId;
  family: FamilySummary;
  view: BoardView;
  rowIds: readonly RowId[];
  showDate: boolean;
  selected: ReadonlySet<BookId>;
  format: OddsDisplay;
  drill: Drill;
  collapsed: ReadonlySet<string>;
  bet: BetActions;
  onToggle: (eventId: string) => void;
  onOpen: (rowId: RowId, slot: number, outcome: number, main: number | null) => void;
  onHide: (row: HiddenRow) => void;
}) {
  const noun = countNoun(family);
  const items = useMemo(() => resultItems(eventGroups(view, rowIds, collapsed, "full", showDate, league)), [view, rowIds, collapsed, showDate, league]);
  return (
    <div className="flex min-h-0 flex-grow flex-col">
      <VirtuosoErrorBoundary>
        <Virtuoso
          className="scrollbar-custom scrollbar-color-primary"
          style={{ height: "100%", width: "100%" }}
          data={items}
          increaseViewportBy={{ top: 200, bottom: 200 }}
          computeItemKey={(_, item) => (item.kind === "event" ? `g:${item.group.eventId}` : `r:${item.rowId}`)}
          components={{ Footer: () => <div className="h-3" /> }}
          itemContent={(_, item) => {
            if (item.kind === "event") {
              const { group } = item;
              return (
                <div className="px-3 pt-3">
                  <EventGroupHeader label={group.label} date={group.date} time={group.time} count={group.rowIds.length} noun={noun} collapsed={group.collapsed} variant="card" onToggle={() => onToggle(group.eventId)} />
                </div>
              );
            }
            return <MobileResultRow league={league} family={family} rowId={item.rowId} event={view.events.get(item.eventId)} selected={selected} format={format} drill={drill} last={item.last} bet={bet} onOpen={onOpen} onHide={onHide} />;
          }}
        />
      </VirtuosoErrorBoundary>
    </div>
  );
}

export function MobileDrillSheet({
  target, family, event, entityName, avatar, selected, listed, format, minStake, isMobile, bookStake, hasAccount, onClose, onNavigate, onHide, onLinkClick, onOpenHistory,
}: {
  target: DrillTarget;
  family: FamilySummary;
  event: EventHeader | undefined;
  entityName: string | null;
  avatar: ReactNode;
  selected: ReadonlySet<BookId>;
  listed: ReadonlySet<BookId>;
  format: OddsDisplay;
  minStake: number;
  isMobile: boolean;
  bookStake: BookStake;
  hasAccount: HasAccount;
  onClose: () => void;
  onNavigate: (patch: Partial<DrillTarget>) => void;
  onHide: () => void;
  onLinkClick: RailLinkClick;
  onOpenHistory: OpenOddsHistory;
}) {
  const specs = family.markets;
  const { spec, model, modelAt, opens, loading } = useRailModel(target, family, event, selected, listed, format, minStake, entityName);
  const isProp = family.kind === "props";
  const subject = subjectKind(family, entityName !== null);
  const matchup = matchupLabel(event, target.league);
  const title = entityName ?? matchup;
  const order = marketDisplayOrder(specs);
  const summaries = model?.summaries ?? [];

  const header = (
        <div className="flex items-start gap-2 pb-2.5 pl-4 pr-2">
          <div className="mt-0.5 flex-shrink-0">{avatar}</div>
          <div className="flex min-w-0 flex-grow flex-col gap-1 pt-0.5">
            {(isProp || specs.length <= 1) && <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-zinc-500">{spec.label}</span>}
            <h2 className={cn(family.layout === "results" ? "line-clamp-2 break-words" : "truncate", "text-[17px] font-semibold leading-[22px] text-foreground")} style={{ fontFamily: "'Plus Jakarta Sans', Inter, system-ui, sans-serif" }}>{title}</h2>
            {!isProp && entityName !== null && <span className="truncate text-[12px] text-zinc-400">{matchup}</span>}
            {event && (event.live
              ? <><LiveState live={event.live} className="self-start" /><LatestPlay play={event.live.plays[0] ?? null} /><span className="data-num text-[12px] text-zinc-500">Started {startedAt(event.startMs)}</span></>
              : <span className="data-num text-[12px] text-zinc-500">{metaDate(event.startMs)} · <span className={KICKOFF_TIME}>{clock(event.startMs)}</span></span>)}
          </div>
          <button type="button" onClick={onHide} aria-label={subject === "game" ? "Hide game from this board" : `Hide ${entityName ?? subject} from this board`} className="flex h-11 flex-shrink-0 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium text-zinc-300 active:bg-white/[0.08]">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" /><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" /><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" /><path d="m2 2 20 20" /></svg>
            <span>Hide</span>
          </button>
          <button type="button" aria-label="Close comparison" onClick={onClose} className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-zinc-400">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>
  );
  return (
    <SheetShell open title={`Compare books for ${title}`} onClose={onClose} header={header} maxHeight="92svh">
        {!isProp && specs.length > 1 && (
          <div role="tablist" aria-label="Markets for this game" className="grid h-11 flex-shrink-0 grid-cols-3 border-b border-surface-line px-4">
            {order.map((slot) => (
              <button key={specs[slot].id} type="button" role="tab" aria-selected={slot === target.slot} onClick={() => onNavigate({ slot, line: null })} className={cn("text-center text-[14px] font-medium", slot === target.slot ? "text-foreground shadow-[inset_0_-2px_0_#00d4ff]" : "text-zinc-400")}>{specs[slot].label}</button>
            ))}
          </div>
        )}
        {summaries.length > 1 && <LineTabs summaries={summaries} spec={spec} outcome={target.outcome} lineValue={model?.lineValue ?? null} touch onNavigate={onNavigate} />}
        <div className="noscroll min-h-0 flex-grow overflow-y-auto pb-[max(1rem,env(safe-area-inset-bottom))]">
          {model && !event?.live && (
            <LineHistoryRow target={target} spec={spec} event={event} entityName={entityName} model={model} modelAt={modelAt} bookStake={bookStake} isMobile onOpenHistory={onOpenHistory} />
          )}
          <div className="px-4 pt-3">
            {loading ? (
              <div className="py-10 text-center text-[12px] text-zinc-500">Loading books…</div>
            ) : model && model.columns.length > 0 ? (
              <RailColumns columns={model.columns} opens={opens} bookStake={bookStake} highlightOutcome={target.outcome} hasAccount={hasAccount} onLinkClick={onLinkClick} isMobile={isMobile} rowClassName="min-h-[44px]" />
            ) : (
              <div className="py-10 text-center text-[12px] text-zinc-500">No books at this line.</div>
            )}
          </div>
        </div>
    </SheetShell>
  );
}
